import React, { useState, useCallback } from "react";
import Modal from "react-bootstrap/Modal";
import ApplicantForm from "../Forms/ApplicantForm";
import ContractorForm from "../Forms/Contractor";
import LocationForm from "../Forms/Location";
import { validateField } from "../../utils/validation/validation";
import Tab from "react-bootstrap/Tab";
import Tabs from "react-bootstrap/Tabs";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import { Row, Button } from "react-bootstrap";
import ListGroup from "react-bootstrap/ListGroup";
import InputField from "../../utils/InputField";
import { updateApplication } from "../../services/api";
import { useToast } from "../../contexts/ToastContext";
import { useLoading } from "../../contexts/LoadingContext";
import Dropzone from "../../utils/dropzone";

function ApplicationModal({ show, onHide, data }) {
  const { showToast } = useToast();
  const { startLoading, stopLoading } = useLoading();
  const initialFormValue = {
    applicant: {
      firstName: data?.firstName || "",
      lastName: data?.lastName || "",
      address: data?.address || "",
      email: data?.email || "",
      phoneNumber: data?.phoneNumber || "",
      communityEmail: data?.communityEmail || "",
      notes: data?.notes || "",
      role: data?.role || "",
    },
    contractors: data?.contractors || [],
    location: {
      address: data?.location?.address || "",
      permitType: data?.location?.permitType || "Residential",
      description: data?.location?.description || "",
      cost: data?.location?.cost || "",
      locationFiles: data?.location?.locationFiles || "",
    },
    app: {
      status: data?.status || "Pending",
      permitFee: data?.permitFee || "",
      planReviewFee: data?.planReviewFee || "",
      inspectionsFee: data?.inspectionsFee || "",
      permitNumber: data?.permitNumber || "",
      standardReport: data?.standardReport || "",
      files: data?.location.files,
    },
  };

  const intialContractorFields = {
    individualName: "",
    trade: "",
    email: "",
    phoneNumber: "",
    address: "",
    license: "",
    licenseLink: "",
  };
  const [formData, setFormData] = useState(initialFormValue);
  const [selectedContractorIndex, setSelectedContractorIndex] = useState(null);
  const [addOrUpdateButtonText, setAddOrUpdateButtonText] =
    useState("Add Contractor");
  const [currentContractor, setCurrentContractor] = useState(
    intialContractorFields
  );
  const [errors, setErrors] = useState({
    applicant: {},
    contractors: [],
    location: {},
    community: null,
  });
  const validateContractorFields = () => {
    const errors = {};
    const requiredFields = [
      "individualName",
      "trade",
      "email",
      "phoneNumber",
      "address",
    ];

    requiredFields.forEach((field) => {
      const error = validateField(field, currentContractor[field]);
      if (error) {
        errors[field] = error;
      }
    });

    setErrors((prevErrors) => ({
      ...prevErrors,
      currentContractor: errors,
    }));

    return Object.keys(errors).length === 0;
  };
  const handleAddOrUpdateContractor = () => {
    if (validateContractorFields()) {
      setFormData((prevData) => {
        const updatedContractors = [...prevData.contractors];
        if (selectedContractorIndex !== null) {
          updatedContractors[selectedContractorIndex] = currentContractor;
        } else {
          updatedContractors.push(currentContractor);
        }
        return { ...prevData, contractors: updatedContractors };
      });
      setCurrentContractor(intialContractorFields);
      setSelectedContractorIndex(null);
      setAddOrUpdateButtonText("Add Contractor");
    }
  };
  const handleSelectContractor = (index) => {
    setCurrentContractor(formData.contractors[index]);
    setSelectedContractorIndex(index);
    setAddOrUpdateButtonText("Update Contractor");
  };
  const handleDataChange = useCallback((section, field, value) => {
    setFormData((prevData) => ({
      ...prevData,
      [section]: { ...prevData[section], [field]: value },
    }));
  }, []);
  const handleValidate = useCallback((section, field, error) => {
    setErrors((prevErrors) => ({
      ...prevErrors,
      [section]: { ...prevErrors[section], [field]: error },
    }));
  }, []);

  const validateEntireForm = () => {
    const applicantErrors = {};
    const locationErrors = {};
    const contractorErrors = formData.contractors.map(() => ({}));

    let isValid = true;

    Object.entries(formData.applicant).forEach(([field, value]) => {
      const error = validateField(field, value);
      if (error) {
        applicantErrors[field] = error;
        isValid = false;
      }
    });

    Object.entries(formData.location).forEach(([field, value]) => {
      const error = validateField(field, value);
      if (error) {
        locationErrors[field] = error;
        isValid = false;
      }
    });

    setErrors({
      applicant: applicantErrors,
      location: locationErrors,
      contractors: contractorErrors,
    });
    return isValid;
  };

  const handleFileUpload = useCallback((fieldName, uploadedFiles) => {
          console.log('Files uploaded to field:', fieldName, uploadedFiles);
          handleDataChange('app',fieldName, uploadedFiles); 
  }, [handleDataChange]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const isValid = validateEntireForm();
    if (!isValid) return;
    if (addOrUpdateButtonText === "Update Contractor") {
      showToast("Please update contractor to save data", "error");
      return;
    }
    try {
      startLoading();
      await updateApplication(data.applicantId, formData);
      showToast("Update successfully!", "success");
      onHide();
    } catch (error) {
      showToast(error.message || "Update failed. Please try again.", "error");
    } finally {
      stopLoading();
    }
  };

  const applicationStatus = data ? data.status : "Pending";
  return (
    <Modal
      show={show}
      onHide={onHide}
      size="lg"
      aria-labelledby="add-community-modal"
      className="add-community-modal"
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title className="text-uppercase" id="add-community-modal">
          Edit Application
        </Modal.Title>
        {data && (
          <div
            className={`modal-header-status status-${applicationStatus.toLowerCase()}`}
          >
            Status:{" "}
            <span className={`span-${applicationStatus.toLowerCase()}`}>
              {applicationStatus}
            </span>
          </div>
        )}
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={handleSubmit}>
          <div className="form-wrapper mb-5 form-modal">
            <Row>
              {data && (
                <Col md="4">
                  <Row className="mb-3">
                    <InputField
                      field="status"
                      label="Status"
                      value={formData.app.status}
                      onChange={(field, value) =>
                        handleDataChange("app", field, value)
                      }
                      error={errors.status}
                      type="select"
                      options={["Pending", "Approved"]}
                      as={Col}
                      controlId="applicantStatus"
                    />
                  </Row>
                  <Row className="mb-3">
                    <InputField
                      field="permitNumber"
                      label="Permit Number"
                      value={formData.app.permitNumber}
                      onChange={(field, value) =>
                        handleDataChange("app", field, value)
                      }
                      required={false}
                      as={Col}
                    />
                  </Row>

                  <Row className="mb-3">
                    <InputField
                      field="permitFee"
                      label="Permit Fee"
                      value={formData.app.permitFee}
                      onChange={(field, value) =>
                        handleDataChange("app", field, value)
                      }
                      required={false}
                      as={Col}
                    />
                  </Row>
                  <Row className="mb-3">
                    <InputField
                      field="planReviewFee"
                      label="Plan Review Fee"
                      value={formData.app.planReviewFee}
                      onChange={(field, value) =>
                        handleDataChange("app", field, value)
                      }
                      required={false}
                      as={Col}
                    />
                  </Row>
                  <Row className="mb-3">
                    <InputField
                      field="inspectionsFee"
                      label="Inspections Fee"
                      value={formData.app.inspectionsFee}
                      onChange={(field, value) =>
                        handleDataChange("app", field, value)
                      }
                      required={false}
                      as={Col}
                    />
                  </Row>
                  <Row className="mb-3">
                      <div className="form-heading mt-5 text-uppercase fw-bold">
                            Standard Reports (Only PDF)
                        </div>
                        <Dropzone fileTypes={['application/pdf']} showPreview={true} fieldName="standardReport" onFileUpload={handleFileUpload} />
                        {(data?.standardReport?.length > 0) && (
                            <Row>
                                <Col sm="12">
                                    <div className="form-wrapper rounded mt-2">
                                        <div className="form-heading text-uppercase fw-bold">
                                            Download/View Standard Report
                                        </div>
                                        <ListGroup as="ul">
                                            {data?.standardReport.map((file, index) => (
                                                <ListGroup.Item as="li" key={index}>
                                                    <a href={file.link} target="_blank" rel="noopener noreferrer">
                                                        File {index + 1}
                                                    </a>
                                                </ListGroup.Item>
                                            ))}
                                        </ListGroup>
                                    </div>
                                </Col>
                            </Row>
                        )}
                  </Row>
                </Col>
              )}
              <Col md="8">
                <Tabs
                  defaultActiveKey="applicant"
                  id="uncontrolled-tab-example"
                  className="mb-3"
                >
                  <Tab eventKey="applicant" title="Applicant">
                    <ApplicantForm
                      data={formData.applicant}
                      onChange={(field, value) =>
                        handleDataChange("applicant", field, value)
                      }
                      onValidate={(field, error) =>
                        handleValidate("applicant", field, error)
                      }
                      errors={errors.applicant || {}}
                      isFrom={"modal"}
                    />
                  </Tab>
                  <Tab eventKey="contractor" title="Contractor">
                    <ContractorForm
                      data={currentContractor}
                      contractors={formData.contractors}
                      onChange={(field, value) =>
                        setCurrentContractor({
                          ...currentContractor,
                          [field]: value,
                        })
                      }
                      onValidate={(field, error) =>
                        handleValidate("currentContractor", field, error)
                      }
                      onAddOrUpdateContractor={handleAddOrUpdateContractor}
                      onSelectContractor={handleSelectContractor}
                      errors={errors.currentContractor || {}}
                      buttonText={addOrUpdateButtonText}
                      isFrom={"modal"}
                    />
                  </Tab>
                  <Tab eventKey="location" title="Location">
                    <LocationForm
                      data={formData.location}
                      onChange={(field, value) =>
                        handleDataChange("location", field, value)
                      }
                      onValidate={(field, error) =>
                        handleValidate("location", field, error)
                      }
                      errors={errors.location || {}}
                      isFrom={"modal"}
                    />
                    <Row>
                      <Col sm="12">
                        <div className="form-wrapper rounded mt-2">
                          <div className="form-heading text-uppercase fw-bold">
                            Download/View Files
                          </div>
                          <ListGroup as="ul">
                            {formData.app?.files.map((file, index) => (
                              <ListGroup.Item as="li" key={index}>
                                <a
                                  href={file.link}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  File {index + 1}
                                </a>
                              </ListGroup.Item>
                            ))}
                          </ListGroup>
                        </div>
                      </Col>
                    </Row>
                  </Tab>
                </Tabs>
              </Col>
            </Row>
          </div>
          <div className="text-end">
            <Button type="submit" className="btn btn-submit">
              Save Changes
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
}

export default ApplicationModal;
